import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './Nexsets.module.scss';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_CONNECT, ROUTE_DISCOVER } from 'new/constants/pathnames';
import { SearchBanner } from 'new/components/discover/Nexsets/components/SearchBanner';
import { NexsetCard } from 'new/components/discover/NexsetCard';
import { Stack } from 'new/elements/Stack';
import { VirtuosoGrid } from 'react-virtuoso';
import { DiscoverContext } from 'new/components/discover/Discover';
import { Box } from 'new/elements/Box';
import styled from '@emotion/styled';
import { Nexset } from 'new/entities/Nexset';
import { isEmpty, pick, size } from 'lodash';
import { useSelector } from 'react-redux';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_AVAILABLE_DATASETS_REQUEST,
  GET_DATA_SETS_REQUEST,
  GET_PUBLIC_DATASETS_REQUEST,
} from 'js/constants/actionTypes';
import Typography from 'new/elements/Typography';
import { Skeleton } from '@mui/material';
import {
  loadDataSets,
  loadMyAvailableDataSets,
  resetPageNumbers,
  setContextualSupport,
  setTotalItems,
  setTotalPages,
  unsetContextualSupport,
} from 'js/actions';
import { Link } from 'react-router-dom';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import { SUPPORT_KEY_DATASET_LIST } from 'js/constants/supportPageKeys';
import { AvailableFilterTypesByView, SearchFilterViews } from 'js/constants/visibilityFilters';
import {
  applyFilters,
  areSegmentsDifferent,
  processClientSideSearchFilters,
} from 'js/lib/SearchFilterUtils';
import { getMyAvailableDatasetEntities, getMyDataSetEntities } from 'js/reducers/entities';
import { ExclusiveButtons } from 'new/elements/ExclusiveButtons';
import { ModalAddToMarketplace } from 'new/elements/ModalAddToMarketplace/ModalAddToMarketplace';
import { DetailView } from 'new/elements/DetailView';
import useAppDispatch from 'js/hooks/useAppDispatch';
import useEntities from 'new/state/entities/repository/useEntities';
import { useHandleAccessLevelChanges } from 'new/hooks/useHandleAccessLevelChanges';
import { useAppRepository } from 'new/state/app/repository/useAppRepository';
import { useUserRepository } from 'new/state/users';

interface NexsetsProps {}

const ItemContainer = styled.div`
  padding: 0.5rem;
  width: 33%;
  display: flex;
  flex: none;
  align-content: stretch;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    width: 50%;
  }

  @media (max-width: 300px) {
    width: 100%;
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
` as React.FC;

const dataSetAccessControls = [
  {
    label: 'All Nexsets',
    value: 'all',
  },
  {
    label: 'Data Shared with Me',
    value: 'shared',
  },
];

const Placeholder = ({ onlyContent }: { onlyContent?: boolean } & any) => {
  const Content = (
    <Box className={styles.cardLoader}>
      <Stack padding="16px">
        <Skeleton
          variant="rounded"
          animation="wave"
          width="90%"
          height="1.4rem"
          style={{ marginBottom: 'var(--spacing-md)' }}
        />

        <Skeleton
          variant="rounded"
          animation="wave"
          className={styles.placeholderLine}
          width="85%"
          height="14px"
        />

        <Stack marginTop="32px">
          <Stack height="32px" direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="rounded" animation="wave" height="14px" width="20%" />

            <Skeleton variant="rounded" animation="wave" height="14px" width="20%" />
          </Stack>
          <Stack height="32px" direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="rounded" animation="wave" height="14px" width="20%" />

            <Skeleton variant="rounded" animation="wave" height="14px" width="20%" />
          </Stack>
          <Stack height="32px" direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="rounded" animation="wave" height="14px" width="20%" />

            <Skeleton variant="rounded" animation="wave" height="14px" width="20%" />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );

  if (onlyContent) {
    return Content;
  }
  return <ItemContainer>{Content}</ItemContainer>;
};

export const Nexsets: ScreenComponent<NexsetsProps> = () => {
  const dispatch = useAppDispatch();
  const { data: entities } = useEntities();
  const { getUserById } = useUserRepository();

  const dataSetSchemas = useSelector((state: any) => state.dataSetSchemas);

  const app = useAppRepository();
  const {
    listing: { currentPage, sortCriteria },
    visibilityFilters: { filters: allFilters, availableFilters },
  } = app;

  const nexsets = useSelector(getMyDataSetEntities);
  const availableDatasets = useSelector(getMyAvailableDatasetEntities);
  const prevFilters = useRef();
  const [nexsetId, setNexset] = useState(null);
  const [dataSetAccess, setDataSetAccess] = useState('all');
  const { data: nexset } = useEntities(`dataSets[${nexsetId}]`);
  const loading = useIsLoading(
    GET_DATA_SETS_REQUEST,
    GET_AVAILABLE_DATASETS_REQUEST,
    GET_PUBLIC_DATASETS_REQUEST,
  );
  const { containerRef } = useContext(DiscoverContext);
  const overlayRef = useRef<HTMLDivElement>(null);
  const timeoutEnterRef = useRef();
  const showAvailableDataSets = dataSetAccess === 'shared';
  const view = showAvailableDataSets
    ? SearchFilterViews.AVAILABLE_DATA_SETS
    : SearchFilterViews.MY_DATA_SETS;
  const filters = allFilters[view];
  const hasFiltersEnabled = !isEmpty(filters);
  const filteredAvailableDataSets = useMemo(() => {
    const { filteredObjects } = applyFilters(
      availableDatasets,
      processClientSideSearchFilters(
        filters,
        pick(availableFilters, AvailableFilterTypesByView[SearchFilterViews.AVAILABLE_DATA_SETS]),
      ),
      entities,
      dataSetSchemas,
    );

    return filteredObjects as any[];
  }, [availableDatasets, filters]);

  const data = showAvailableDataSets ? filteredAvailableDataSets : nexsets;

  useEffect(() => {
    dispatch(resetPageNumbers());
    dispatch(setContextualSupport(SUPPORT_KEY_DATASET_LIST));

    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_DATASET_LIST));
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [currentPage, sortCriteria]);

  useEffect(() => {
    if (filters && prevFilters.current && areSegmentsDifferent(filters, prevFilters.current)) {
      loadData();
    }

    prevFilters.current = filters;
  }, [filters]);

  const loadData = () => {
    if (!showAvailableDataSets) {
      const filters = pick(
        availableFilters,
        AvailableFilterTypesByView[SearchFilterViews.MY_DATA_SETS],
      );

      const thunk = loadDataSets(currentPage || 1, sortCriteria, filters);

      dispatch(thunk).then((response: any) => {
        const { totalPages, totalItems } = response;
        if (!totalItems && !totalPages) return;

        dispatch(setTotalPages(totalPages));
        dispatch(setTotalItems(totalItems));
      });
    }
  };

  useHandleAccessLevelChanges(loadData);

  const onResourceSelected = (id: number) => {
    setNexset(id);
  };

  const onClickDetails = (nexset: Nexset) => {
    setNexset(nexset?.id);
  };

  const onCloseDrawer = () => {
    setNexset(null);
  };

  const onClickRequest = () => {};

  const onDataSetAccessChange = (_, value) => {
    setDataSetAccess(value);

    if (value === 'shared') {
      dispatch(loadMyAvailableDataSets());
    }
  };

  const ContentView = (() => {
    if (!loading && !size(data)) {
      return (
        <Typography className={styles.emptyResults} variant="h5">
          {hasFiltersEnabled ? (
            'No results match your search criteria.'
          ) : (
            <>
              Looks empty here. Create a{' '}
              <Link to={`${ROUTE_REDESIGN}${ROUTE_CONNECT}/flows?action=create`}>
                New Data Flow
              </Link>{' '}
              to begin
            </>
          )}
        </Typography>
      );
    }
    return (
      <VirtuosoGrid
        customScrollParent={containerRef.current}
        data={loading ? new Array(12).fill(0) : data}
        components={{
          List: ListContainer,
          Item: ItemContainer,
          ScrollSeekPlaceholder: Placeholder,
        }}
        itemContent={(index) =>
          loading ? (
            <Placeholder onlyContent />
          ) : (
            <NexsetCard
              onClickDetails={onClickDetails}
              onClickRequest={onClickRequest}
              nexset={data[index]}
              timeoutEnterRef={timeoutEnterRef}
              overlayRef={overlayRef}
              owner={getUserById(data[index].owner)}
            />
          )
        }
      />
    );
  })();

  return (
    <>
      <DetailView
        data={nexset ? { ...nexset, type: 'DISCOVER' } : undefined}
        onClose={onCloseDrawer}
      />

      <SearchBanner view={view} onResourceSelected={onResourceSelected} />
      <div className={`data-set-access-switch-container ${styles.accessSwitchContainer}`}>
        <ExclusiveButtons
          data={dataSetAccessControls}
          value={dataSetAccess}
          onChange={onDataSetAccessChange}
        />
      </div>
      <Stack className={styles.nexsetsContainer} padding="16px">
        <div ref={overlayRef} className={styles.overlay} />
        {ContentView}
      </Stack>
    </>
  );
};

Nexsets.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_DISCOVER}/nexsets`,
  pageTitle: 'Discover',
  modals: [ModalFilePreview, ModalAddToMarketplace],
  screenName: 'Nexsets',
  domain: 'discover',
};
